import React, { useEffect } from 'react'

import DataFallback from 'components/DataFallback'
import type { DataFallbackConfigType } from 'components/DataFallback/types'
import { DataFallbackTypeEnum } from 'components/DataFallback/types'
import { PROJECT_HOME_PATHS } from 'constants/paths'
import { courseStart } from 'services/Store/Project/actions'
import { setIncompleteSections } from 'services/Store/Project/actions'
import { SectionTypeEnum } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import {
  getCourseState,
  getProgress,
  getSection,
  getSectionId,
} from 'services/Store/Project/selectors'
import type { IBlockMode } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import { useSectionViewed } from 'services/lms/hooks/useSectionViewed'

import CourseCover from './CourseCover'
import CourseLanding from './CourseLanding'
import CourseTestSection from './CourseTestSection'

const CONFIG: DataFallbackConfigType = {
  position: 'static',
  backUrl: { path: PROJECT_HOME_PATHS.sections },
  title: t('page.notFound.sectionNotFound'),
  linkLabel: t('page.notFound.textLink.sections'),
  type: DataFallbackTypeEnum.notFound,
}

const CourseSectionMap = {
  [SectionTypeEnum.cover]: CourseCover,
  [SectionTypeEnum.landing]: CourseLanding,
  [SectionTypeEnum.test]: CourseTestSection,
  [SectionTypeEnum.chapter]: null,
}

const useSectionCompletedWatch = ({
  sectionId,
  progress,
}: {
  sectionId?: string
  progress?: number
}) => {
  // Uncomment if want to use outside of CourseSection (useReduxUpdate.ts)
  // const sectionId = useProjectContext(getSection)?.id
  // const progress = useProjectContext(getProgress, sectionId)
  const dispatch = useProjectDispatch()
  const notCompletedSections = useProjectContext(getCourseState).notCompletedSections || []

  useEffect(() => {
    if (progress === 100 && sectionId) {
      const filtered = notCompletedSections.filter((id) => id !== sectionId)
      dispatch(setIncompleteSections(filtered))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, progress, sectionId])
}

const CourseSection: React.FC<{ mode: IBlockMode }> = ({ mode }) => {
  const dispatch = useProjectDispatch()
  const section = useProjectContext(getSection)
  const sectionId = useProjectContext(getSectionId)
  const cover = useProjectContext(getSection, { cover: true })
  const firstSection = useProjectContext(getSection, { first: true })
  const progress = useProjectContext(getProgress, sectionId)

  const currentSection = sectionId ? section : cover
  const Section = (currentSection?.type && CourseSectionMap[currentSection?.type]) || CourseCover

  const startWithoutCover = Boolean(!sectionId && !cover && firstSection)

  useSectionCompletedWatch({ sectionId, progress })
  useSectionViewed({ mode })

  useEffect(() => {
    if (startWithoutCover) {
      dispatch(courseStart())
    }
  }, [dispatch, startWithoutCover])

  return (
    <DataFallback
      config={CONFIG}
      data={currentSection}
      error={!currentSection}
      loading={startWithoutCover}
    >
      {(currentSection) => <Section mode={mode} section={currentSection} />}
    </DataFallback>
  )
}

export default CourseSection
